/**
 * Values that must agree between the API and the web app.
 *
 * Anything duplicated as a literal in two places eventually drifts, so it lives
 * here instead.
 */
/** Prefix for every versioned REST route. */
export const API_PREFIX = 'api/v1';
// -----------------------------------------------------------------------------
// Pagination
// -----------------------------------------------------------------------------
export const DEFAULT_PAGE_SIZE = 12;
export const MAX_PAGE_SIZE = 100;
// -----------------------------------------------------------------------------
// Auth cookie names
// -----------------------------------------------------------------------------
/**
 * Tokens are delivered as httpOnly cookies rather than in a JSON body that the
 * SPA would have to keep in `localStorage`. `localStorage` is readable by any
 * script on the page, so a single XSS bug there means silent, persistent
 * account takeover; an httpOnly cookie is not reachable from JavaScript at all.
 * The trade-off is that we must defend against CSRF, which is what the
 * double-submit token below is for.
 */
export const ACCESS_TOKEN_COOKIE = 'tbbt_at';
export const REFRESH_TOKEN_COOKIE = 'tbbt_rt';
export const CSRF_COOKIE = 'tbbt_csrf';
export const CSRF_HEADER = 'x-csrf-token';
/** Header carrying a draft preview token for unpublished content. */
export const PREVIEW_TOKEN_HEADER = 'x-preview-token';
export const PREVIEW_TOKEN_QUERY = 'preview';
// -----------------------------------------------------------------------------
// Site settings keys
// -----------------------------------------------------------------------------
/**
 * Settings are a typed key/value store rather than a wide table, so the client
 * can adjust marketing content that would otherwise be hard-coded — and so the
 * fabricated proof points in the reference design (client logo wall, headline
 * statistics, office address) are data the client owns rather than lies baked
 * into the bundle. See docs/CONTENT-BRIEF.md.
 */
export const SettingKey = {
    /** Brand name, tagline, logo, primary CTA label. */
    BRAND: 'brand',
    /** Office locations and contact channels shown in the footer and contact page. */
    CONTACT: 'contact',
    /** Social profile URLs. */
    SOCIAL: 'social',
    /** Headline statistics band. Ships disabled with empty values — see brief. */
    STATS: 'stats',
    /** "Trusted by" logo wall. Ships disabled and empty — see brief. */
    CLIENT_LOGOS: 'clientLogos',
    /** Default meta title/description/OG image for pages that don't set their own. */
    SEO_DEFAULTS: 'seoDefaults',
    /** Leadership team cards. */
    TEAM: 'team',
    /**
     * The images used by the homepage's two image-led blocks — the hero and the
     * "Why we exist" split.
     *
     * A setting rather than a bundled asset for the same reason the logo is: the
     * picture at the top of the homepage is content, and changing it should not need
     * a deploy. Both slots are nullable, and each block falls back to the abstract
     * composition it shipped with when its slot is empty.
     */
    HOMEPAGE_MEDIA: 'homepageMedia',
    /** Feature toggles for optional homepage sections. */
    FEATURE_FLAGS: 'featureFlags',
};
export const SETTING_KEYS = Object.values(SettingKey);
/**
 * Settings readable without authentication. Everything not listed here is
 * admin-only, so adding a setting cannot accidentally leak it to the public
 * API — the default is private.
 */
export const PUBLIC_SETTING_KEYS = [
    SettingKey.BRAND,
    SettingKey.CONTACT,
    SettingKey.SOCIAL,
    SettingKey.STATS,
    SettingKey.CLIENT_LOGOS,
    SettingKey.SEO_DEFAULTS,
    SettingKey.TEAM,
    SettingKey.HOMEPAGE_MEDIA,
    SettingKey.FEATURE_FLAGS,
];
// -----------------------------------------------------------------------------
// Static page registry
// -----------------------------------------------------------------------------
/**
 * `componentKey` links a STATIC page row in the database to the React component
 * that renders it. The database owns the slug, visibility, ordering and SEO;
 * the code owns the layout.
 *
 * Adding a value here is a two-step change: add the key, then register the
 * component in `apps/web/src/pages/public/registry.tsx`. The web app fails
 * loudly at startup if a STATIC page references a key with no component, which
 * is far easier to debug than a blank screen.
 */
export const StaticPageComponent = {
    HOME: 'home',
    SERVICES: 'services',
    SERVICE_DETAIL: 'serviceDetail',
    ABOUT: 'about',
    /**
     * The full leadership team.
     *
     * The homepage and About show the first few people and link here; this is the page that
     * lists everybody, so the complete team has one canonical address rather than being
     * duplicated across two pages.
     */
    TEAM: 'team',
    BLOG_INDEX: 'blogIndex',
    BLOG_POST: 'blogPost',
    CONTACT: 'contact',
    /** Built but seeded hidden — Phase 2 per the client brief. */
    CAREERS: 'careers',
    PRIVACY: 'privacy',
    TERMS: 'terms',
    COOKIES: 'cookies',
};
// -----------------------------------------------------------------------------
// Services offered
// -----------------------------------------------------------------------------
/**
 * The four services the client named on the call. The reference design's fourth
 * card said "Contract Staffing"; the brief says on-demand talent sourcing
 * ("rent a recruiter"), so that is what ships. Contract staffing was mentioned
 * in passing and is kept as a sub-capability of on-demand sourcing rather than a
 * peer service.
 */
export const ServiceSlug = {
    EXECUTIVE_SEARCH: 'executive-search',
    PERMANENT_HIRING: 'permanent-hiring',
    RPO: 'rpo',
    ON_DEMAND_SOURCING: 'on-demand-talent-sourcing',
};
export const SERVICE_SLUGS = Object.values(ServiceSlug);
// -----------------------------------------------------------------------------
// Validation limits
// -----------------------------------------------------------------------------
export const LIMITS = {
    slug: { min: 1, max: 160 },
    title: { min: 3, max: 200 },
    excerpt: { max: 320 },
    metaTitle: { max: 70 },
    metaDescription: { max: 180 },
    password: { min: 12, max: 128 },
    name: { min: 2, max: 120 },
    inquiryMessage: { min: 10, max: 4000 },
    altText: { max: 300 },
};
/**
 * Reserved slugs. A CUSTOM page may not claim one of these, because doing so
 * would shadow a real application route and make part of the site unreachable.
 */
export const RESERVED_SLUGS = [
    'admin',
    'api',
    'health',
    'login',
    'logout',
    'sitemap.xml',
    'robots.txt',
    'assets',
    'media',
    'preview',
    '_',
];
//# sourceMappingURL=constants.js.map